import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, patch, post } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { FormDialog } from "@/components/common/FormDialog";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Avatar } from "@/components/common/Avatar";
import { SearchInput } from "@/components/common/SearchInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { dateFmt } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const CATEGORIES = ["Tax", "Payroll", "Audit", "Certification", "Legal"];
const FREQUENCIES = ["Monthly", "Quarterly", "Annual", "One-time"];

export default function Compliance() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [frequencyFilter, setFrequencyFilter] = useState("all");
  const { data: types, isLoading } = useQuery({ queryKey: ["compliance"], queryFn: () => get("/compliance") });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => get("/users") });
  const umap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const statuses = useMemo(() => [...new Set((types || []).map((c) => c.status).filter(Boolean))], [types]);
  const filtered = useMemo(() => {
    return (types || []).filter((c) => {
      if (search && !c.name?.toLowerCase().includes(search.toLowerCase())) return false;
      if (statusFilter !== "all" && c.status !== statusFilter) return false;
      if (categoryFilter !== "all" && c.category !== categoryFilter) return false;
      if (frequencyFilter !== "all" && c.frequency !== frequencyFilter) return false;
      return true;
    });
  }, [types, search, statusFilter, categoryFilter, frequencyFilter]);

  const toggle = useMutation({
    mutationFn: ({ id, checklist }) => patch(`/compliance/${id}`, { checklist }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["compliance"] }),
  });
  const create = useMutation({
    mutationFn: (b) => post("/compliance", b),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["compliance"] }); toast.success("Compliance type added"); },
  });

  if (isLoading) return <Skeleton className="h-64" />;
  const urgency = (s) => s === "Overdue" ? "text-red-500" : s === "Action Needed" ? "text-amber-500" : "text-blue-500";

  return (
    <div>
      <PageHeader title="Compliance" subtitle="Upcoming filings, audits and certifications.">
        <FormDialog title="New Compliance Type" testid="new-compliance-modal"
          trigger={<Button data-testid="new-compliance-btn"><Plus className="mr-1.5 h-4 w-4" />New Compliance Type</Button>}
          fields={[
            { name: "name", label: "Name", required: true, full: true },
            { name: "category", label: "Category", type: "select", required: true, options: ["Tax", "Payroll", "Audit", "Certification", "Legal"] },
            { name: "frequency", label: "Frequency", type: "select", required: true, options: ["Monthly", "Quarterly", "Annual", "One-time"] },
            { name: "due_date", label: "Due Date", type: "date" },
            { name: "responsible", label: "Responsible", type: "select", options: users.map((u) => ({ value: u.id, label: u.name })) },
          ]}
          onSubmit={(v) => create.mutateAsync({ ...v, checklist: [] })} />
      </PageHeader>
      {types?.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <SearchInput testid="compliance-search" placeholder="Search name" value={search} onChange={setSearch} />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="compliance-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-40" data-testid="compliance-category-filter"><SelectValue placeholder="All categories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={frequencyFilter} onValueChange={setFrequencyFilter}>
            <SelectTrigger className="w-40" data-testid="compliance-frequency-filter"><SelectValue placeholder="All frequencies" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All frequencies</SelectItem>
              {FREQUENCIES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      {!isLoading && types?.length > 0 && !filtered.length ? (
        <EmptyState icon={ShieldCheck} title="No matching compliance items" description="Adjust your search or filters." />
      ) : (
      <div className="space-y-3">
        {filtered.map((c) => {
          const done = c.checklist.filter((i) => i.checked).length;
          return (
            <div key={c.id} data-testid={`compliance-${c.id}`} className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-start gap-3">
                <ShieldCheck className={cn("h-6 w-6 shrink-0", urgency(c.status))} />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-display text-sm font-semibold">{c.name}</span>
                    <StatusBadge status={c.status} />
                  </div>
                  <div className="text-xs text-muted-foreground">{c.category} · {c.frequency} · Due {dateFmt(c.due_date)}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Avatar user={umap[c.responsible]} size="sm" />
                  <span className="text-xs text-muted-foreground">{done}/{c.checklist.length}</span>
                </div>
              </div>
              <div className="mt-3 space-y-1.5 border-t border-border pt-3">
                {c.checklist.map((item) => (
                  <div key={item.id} className="flex items-center gap-2.5">
                    <Checkbox checked={item.checked} data-testid={`ck-${item.id}`}
                      onCheckedChange={() => toggle.mutate({ id: c.id, checklist: c.checklist.map((x) => x.id === item.id ? { ...x, checked: !x.checked } : x) })} />
                    <span className="flex items-center gap-1.5 text-sm">{item.label}{item.required && <span className="h-1.5 w-1.5 rounded-full bg-red-500" />}</span>
                  </div>
                ))}
              </div>
              <Button size="sm" className="mt-3" data-testid={`complete-${c.id}`} onClick={() => toast.success("Compliance cycle marked complete")}>Mark Completed</Button>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}
